import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useArtistData } from '../../hooks/useArtistData';

const Header = () => {
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut, userProfile, user } = useAuth();
  const { theme, setLightMode, setDarkMode, isDark } = useTheme();
  const { artistData } = useArtistData();

  // Get user information from AuthContext
  const userName = userProfile?.full_name || user?.email || 'User';
  const userEmail = userProfile?.email || user?.email || '';
  const userRole = userProfile?.role || 'user';

  // PRIORITY: App Logo from Promotion Assets first, then Artist Logo from Profile
  const profileIconUrl = artistData?.promotion?.logo || artistData?.profile?.artistLogoUrl;

  const handleLogout = async () => {
    try {
      const { error } = await signOut();
      if (!error) {
        navigate('/login');
      } else {
        console.error('Sign out error:', error);
        // Still navigate to login even if there's an error
        navigate('/login');
      }
    } catch (error) {
      console.error('Sign out error:', error);
      navigate('/login');
    } finally {
      setIsProfileOpen(false);
    }
  };

  const handleHelpSupport = () => {
    navigate('/help-support');
    setIsProfileOpen(false);
  };

  const handleThemeToggle = (mode) => {
    if (mode === 'light') {
      setLightMode();
    } else {
      setDarkMode();
    }
    setIsProfileOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-surface border-b border-border elevation-1">
      <div className="flex items-center justify-between h-16 px-6">
        {/* Logo */}
        <div className="flex items-center space-x-3">
          <div className="flex items-center justify-center w-8 h-8 bg-primary rounded-lg">
            <Icon name="Palette" size={20} color="white" />
          </div>
          <div className="hidden sm:block">
            <h1 className="text-lg font-semibold text-primary">IDArtists</h1>
            <p className="text-xs text-text-secondary -mt-1">
              {userRole === 'artist' ? 'Artist Panel' : 'Admin Panel'}
            </p>
          </div>
        </div>

        {/* User Profile */}
        <div className="flex items-center">
          {/* User Profile Dropdown */}
          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsProfileOpen(!isProfileOpen)}
              className="flex items-center space-x-2 transition-smooth"
            >
              {/* Use App Logo from Promotion Assets first, fallback to profile logo, then default icon */}
              {profileIconUrl ? (
                <div className="w-8 h-8 rounded-full overflow-hidden bg-accent flex items-center justify-center">
                  <img 
                    src={profileIconUrl} 
                    alt="App Logo from Promotion Assets" 
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      // Fallback to default icon if image fails to load
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="w-8 h-8 bg-accent rounded-full items-center justify-center" style={{display: 'none'}}>
                    <Icon name="User" size={16} color="white" />
                  </div>
                </div>
              ) : (
                <div className="w-8 h-8 bg-accent rounded-full flex items-center justify-center">
                  <Icon name="User" size={16} color="white" />
                </div>
              )}
              <div className="hidden sm:block text-left">
                <p className="text-sm font-medium text-text-primary">{userName}</p>
                <p className="text-xs text-text-secondary">{userRole}</p>
              </div>
              <Icon name="ChevronDown" size={16} className="text-text-secondary" />
            </Button>

            {/* Profile Dropdown Menu */}
            {isProfileOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-popover border border-border rounded-lg elevation-2 animate-fade-in">
                <div className="p-3 border-b border-border">
                  <p className="text-sm font-medium text-text-primary">{userName}</p>
                  <p className="text-xs text-text-secondary">{userEmail}</p>
                  <p className="text-xs text-accent capitalize mt-1">{userRole}</p>
                </div>
                <div className="p-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    fullWidth
                    iconName="User"
                    iconPosition="left"
                    iconSize={16}
                    className="justify-start"
                    onClick={() => setIsProfileOpen(false)}
                  >
                    Profile Settings
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    fullWidth
                    iconName="Settings"
                    iconPosition="left"
                    iconSize={16}
                    className="justify-start"
                    onClick={() => setIsProfileOpen(false)}
                  >
                    Preferences
                  </Button>
                  <div className="border-t border-border my-1"></div>
                  {/* Theme Toggle Section */}
                  <div className="px-2 py-1">
                    <p className="text-xs text-text-secondary mb-2">Appearance</p>
                    <div className="flex space-x-1">
                      <Button
                        variant={isDark ? "default" : "ghost"}
                        size="sm"
                        iconName="Moon"
                        iconPosition="left"
                        iconSize={14}
                        className="flex-1 text-xs"
                        onClick={() => handleThemeToggle('dark')}
                      >
                        Dark
                      </Button>
                      <Button
                        variant={!isDark ? "default" : "ghost"}
                        size="sm"
                        iconName="Sun"
                        iconPosition="left"
                        iconSize={14}
                        className="flex-1 text-xs"
                        onClick={() => handleThemeToggle('light')}
                      >
                        Light
                      </Button>
                    </div>
                  </div>
                  <div className="border-t border-border my-1"></div>
                  <Button
                    variant="ghost"
                    size="sm"
                    fullWidth
                    iconName="HelpCircle"
                    iconPosition="left"
                    iconSize={16}
                    className="justify-start"
                    onClick={handleHelpSupport}
                  >
                    Help & Support
                  </Button>
                  <div className="border-t border-border my-1"></div>
                  <Button
                    variant="ghost"
                    size="sm"
                    fullWidth
                    iconName="LogOut"
                    iconPosition="left"
                    iconSize={16}
                    className="justify-start text-destructive hover:text-destructive"
                    onClick={handleLogout}
                  >
                    Sign Out
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;